import { useCallback, useEffect, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
    gesellschaftsvertragAuswerten,
    type Feldvorschlag,
    type VertragsAntwort,
} from "../../services/firmendatenService";

/**
 * Was der Gesellschaftsvertrag über die Firma sagt — und was davon in die
 * Stammdaten soll.
 *
 * Der Vertrag ist die einzige Quelle im Register für den
 * **Unternehmensgegenstand**. Aus ihm folgen zwei Dinge, die KMVS ohnehin
 * führt: die Tätigkeitsbeschreibung und die Rolle im Verbund („Erwerb, Halten
 * und Verwalten von Beteiligungen" ist eine Holding). Dazu kommt das
 * Geschäftsjahr, das ebenfalls nur im Vertrag steht.
 *
 * **Die Tätigkeitsbeschreibung wird ergänzt, nicht ersetzt.** Der notarielle
 * Wortlaut tritt neben das, was die Sachbearbeitung über den Betrieb weiß — das
 * ist oft mehr wert. Sichtbar gemacht wird das durch die Vorschau: Was am Ende
 * im Feld steht, ist zu lesen, bevor man den Haken setzt.
 */
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "14px", lineHeight: 1.5 },
    block: {
        ...shorthands.padding("10px", "12px"),
        ...shorthands.borderRadius("9px"),
        backgroundColor: "#f8f4f8",
        marginBottom: "14px",
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#3d2d39",
    },
    blockZeile: { display: "block", marginTop: "4px" },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("11px", "2px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    eingabe: {
        width: "100%",
        ...shorthands.padding("8px", "10px"),
        marginTop: "4px",
        ...shorthands.borderRadius("8px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13.5px",
        color: "#241522",
        backgroundColor: "#fff",
        ":focus": { ...shorthands.borderColor("#7d2169"), outlineStyle: "none" },
    },
    vorschau: { display: "block", fontSize: "12px", color: "#6b5a66", marginTop: "5px", lineHeight: 1.45 },
    ergaenzt: { color: "#1f7a4d", fontWeight: 700 },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "10px",
        lineHeight: 1.45,
    },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    mitte: { ...shorthands.padding("22px", "0"), textAlign: "center", color: "#6b5a66", fontSize: "13px" },
    verweis: {
        ...shorthands.borderWidth("0"),
        ...shorthands.padding("0"),
        backgroundColor: "transparent",
        color: "#7d2169",
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: 700,
        cursor: "pointer",
        textDecorationLine: "underline",
    },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
});

/**
 * Kontaktfeld → Formularfeld der Akte. Die Maske heißt an zwei Stellen anders
 * als die Spalte; ohne diese Übersetzung landete der Wert im Nichts.
 */
export const VERTRAG_FELDER: Record<Feldvorschlag["feld"], string> = {
    jobOccupation: "taetigkeit",
    financialYearEnd: "financialYearEnd",
    unternehmensfunktion: "unternehmensfunktion",
    gruendungsdatum: "gruendungsdatum",
    vorsteuerAbzugsberechtigt: "vorsteuerAbzugsberechtigt",
    bilanzierungspflichtig: "bilanzierungspflichtig",
    pruefungspflichtHgb: "pruefungspflichtHgb",
    // Rechtsvorgänger: Katalog-Id im Formularfeld `oldLegalFormId`.
    oldLegalForm: "oldLegalFormId",
};

/**
 * Was am Ende im Feld steht. Beim Ergänzen werden Dubletten vermieden — der
 * Gegenstand zweimal untereinander wäre keine Ergänzung, sondern Unordnung.
 */
export function zusammenfuehren(v: Feldvorschlag, wert: string): string {
    const neu = wert.trim();
    const alt = (v.vorhanden ?? "").trim();
    if (!alt || v.modus === "ersetzen") return neu;
    if (!neu) return alt;
    // Die Unternehmensfunktion ist eine Liste mit „; " — dort wird je Wert
    // geprüft, sonst als ganzer Text.
    if (v.feld === "unternehmensfunktion") {
        const da = alt.split(";").map((s) => s.trim()).filter(Boolean);
        const dazu = neu.split(";").map((s) => s.trim()).filter((s) => s && !da.includes(s));
        return [...da, ...dazu].join("; ");
    }
    if (alt.includes(neu)) return alt;
    return `${alt}\n${neu}`;
}

export function GesellschaftsvertragDialog({
    contactId,
    onClose,
    onUebernehmen,
    onDokumentOeffnen,
}: {
    contactId: number;
    onClose: () => void;
    /** Schreibt die Werte in die Maske — gespeichert wird über „Speichern". */
    onUebernehmen: (werte: Array<{ feld: string; wert: string }>) => void;
    onDokumentOeffnen?: (dok: { documentId: number; fileName: string; mimeType: string | null }) => void;
}) {
    const s = useStyles();
    const [laeuft, setLaeuft] = useState(true);
    /** Zweite Stufe: Der Abruf ist durch, die Texterkennung läuft noch. */
    const [erkennung, setErkennung] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [antwort, setAntwort] = useState<VertragsAntwort | null>(null);
    const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
    const [werte, setWerte] = useState<Record<string, string>>({});

    const holen = useCallback(async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            const r = await gesellschaftsvertragAuswerten(contactId, setErkennung);
            setAntwort(r);
            setWerte(Object.fromEntries(r.vorschlaege.map((v) => [v.feld, v.wert])));
            // Vorausgewählt ist, was im Ziel noch leer ist oder ohnehin ergänzt
            // wird — ein belegtes Feld ersetzt nur ein bewusster Haken.
            setGewaehlt(
                new Set(
                    r.vorschlaege
                        .filter((v) => !v.vorhanden || v.modus === "ergaenzen")
                        .map((v) => v.feld),
                ),
            );
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
            setErkennung(false);
        }
    }, [contactId]);

    useEffect(() => {
        void holen();
    }, [holen]);

    const umschalten = (feld: string) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(feld)) neu.delete(feld);
            else neu.add(feld);
            return neu;
        });

    const uebernehmen = () => {
        if (!antwort) return;
        const werteListe = antwort.vorschlaege
            .filter((v) => gewaehlt.has(v.feld))
            .map((v) => ({
                feld: VERTRAG_FELDER[v.feld],
                wert: zusammenfuehren(v, werte[v.feld] ?? v.wert),
            }))
            .filter((w) => w.wert.trim());
        onUebernehmen(werteListe);
        onClose();
    };

    const b = antwort?.befund;
    const rolle = antwort?.verbundrolle;

    return createPortal(
        <div className="kvp-rd-scrim" onClick={laeuft ? undefined : onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">Handelsregister · Gesellschaftsvertrag</div>
                            <h2>{b?.firma || "Gesellschaftsvertrag"}</h2>
                        </div>
                        {!laeuft && (
                            <button className="dm-x" onClick={onClose} aria-label="Schließen">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="dm-body">
                    {laeuft && (
                        <div className={s.mitte}>
                            {erkennung
                                ? "Das Dokument liegt in der Akte — die Texterkennung läuft. Bei einem Scan dauert das etwa eine halbe Minute …"
                                : "Der Registerordner wird gelesen …"}
                        </div>
                    )}

                    {!laeuft && antwort && (
                        <>
                            {antwort.documentId && onDokumentOeffnen && (
                                <div className={s.kopfzeile}>
                                    <button
                                        type="button"
                                        className={s.verweis}
                                        onClick={() =>
                                            onDokumentOeffnen({
                                                documentId: antwort.documentId!,
                                                fileName: antwort.fileName ?? "Gesellschaftsvertrag",
                                                mimeType: antwort.mimeType,
                                            })
                                        }
                                    >
                                        {antwort.fileName || "Vertrag"} öffnen
                                    </button>{" "}
                                    — der Beleg, gegen den geprüft wird.
                                </div>
                            )}

                            {b && (b.gegenstand || b.stammkapital || b.sitz) && (
                                <div className={s.block}>
                                    {b.gegenstand && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Unternehmensgegenstand</span>
                                            <br />
                                            {b.gegenstand}
                                        </span>
                                    )}
                                    {b.sitz && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Sitz</span> {b.sitz}
                                        </span>
                                    )}
                                    {b.stammkapital && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Stammkapital</span>{" "}
                                            {b.stammkapital}
                                        </span>
                                    )}
                                    {rolle && rolle.funktionen.length > 0 && (
                                        <span className={s.blockZeile}>
                                            <span className={s.beschriftung}>Rolle im Verbund</span>{" "}
                                            {rolle.funktionen.join("; ")}
                                            {rolle.begruendung.length > 0 && (
                                                <span className={s.vorschau}>
                                                    {rolle.begruendung.join(" ")}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>
                            )}

                            {antwort.vorschlaege.length === 0 ? (
                                <p>Aus dem Vertrag ließ sich nichts Übernehmbares lesen.</p>
                            ) : (
                                antwort.vorschlaege.map((v) => {
                                    const wert = werte[v.feld] ?? v.wert;
                                    return (
                                        <div key={v.feld} className={s.zeile}>
                                            <input
                                                type="checkbox"
                                                checked={gewaehlt.has(v.feld)}
                                                onChange={() => umschalten(v.feld)}
                                                aria-label={v.bezeichnung}
                                                style={{ marginTop: 4, accentColor: "#7d2169" }}
                                            />
                                            <span className={s.inhalt}>
                                                <span className={s.beschriftung}>{v.bezeichnung}</span>
                                                {/* Bearbeitbar: Der notarielle Wortlaut ist lang und
                                                    nicht immer das, was in der Akte stehen soll. */}
                                                <textarea
                                                    className={s.eingabe}
                                                    rows={v.feld === "jobOccupation" ? 3 : 1}
                                                    value={wert}
                                                    onChange={(ev) =>
                                                        setWerte((alt) => ({ ...alt, [v.feld]: ev.target.value }))
                                                    }
                                                />
                                                {v.begruendung && (
                                                    <span className={s.vorschau}>{v.begruendung}</span>
                                                )}
                                                {v.vorhanden && (
                                                    <span className={s.vorschau}>
                                                        Im Feld steht: „{v.vorhanden}" —{" "}
                                                        {v.modus === "ergaenzen" ? (
                                                            <span className={s.ergaenzt}>
                                                                der Vorschlag wird ergänzt, nicht ersetzt
                                                            </span>
                                                        ) : (
                                                            "wird ersetzt"
                                                        )}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    );
                                })
                            )}

                            {antwort.hinweise.map((h, i) => (
                                <div key={i} className={s.hinweis}>{h}</div>
                            ))}
                        </>
                    )}

                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    <button type="button" className={s.knopf} onClick={onClose}>
                        {antwort?.vorschlaege.length ? "Abbrechen" : "Schließen"}
                    </button>
                    {!!antwort?.vorschlaege.length && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            disabled={laeuft || gewaehlt.size === 0}
                            onClick={uebernehmen}
                        >
                            {gewaehlt.size} in die Maske übernehmen
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
